/**
 * ChatContext.tsx
 * 
 * This context provides chat functionality and state management throughout the application.
 * It handles:
 * 
 * - Chat messages state and history
 * - Current chatbot selection and information
 * - Message sending and receiving
 * - Chat loading states and error handling
 * - Real-time message updates
 * - Chat session management
 * - Message formatting and display
 * 
 * The context manages the chat interface state and provides methods
 * for components to interact with chat functionality, including
 * sending messages, loading chat history, and handling responses.
 */

import React, { createContext, useContext, useReducer, useEffect } from 'react'
import { useAuth } from '../hooks/useAuth'

// Types
interface Message {
  id: string
  content: string
  role: 'user' | 'assistant'
  timestamp: Date
  chatbotId?: string
}

interface Chatbot {
  id: string
  name: string
  description: string
  image: string
  isFree: boolean
  price: string
  creator: string
}

interface ChatState {
  messages: Message[]
  currentChatbot: Chatbot | null
  isLoading: boolean
  error: string | null
  isTyping: boolean
}

type ChatAction =
  | { type: 'SET_LOADING'; payload: boolean }
  | { type: 'SET_ERROR'; payload: string | null }
  | { type: 'SET_CURRENT_CHATBOT'; payload: Chatbot | null }
  | { type: 'ADD_MESSAGE'; payload: Message }
  | { type: 'SET_MESSAGES'; payload: Message[] }
  | { type: 'CLEAR_MESSAGES' }
  | { type: 'SET_TYPING'; payload: boolean }

// Initial state
const initialState: ChatState = {
  messages: [],
  currentChatbot: null,
  isLoading: false,
  error: null,
  isTyping: false,
}

// Reducer
const chatReducer = (state: ChatState, action: ChatAction): ChatState => {
  switch (action.type) {
    case 'SET_LOADING':
      return {
        ...state,
        isLoading: action.payload,
      }
    case 'SET_ERROR':
      return {
        ...state,
        error: action.payload,
      }
    case 'SET_CURRENT_CHATBOT':
      return {
        ...state,
        currentChatbot: action.payload,
      }
    case 'ADD_MESSAGE':
      return {
        ...state,
        messages: [...state.messages, action.payload],
      }
    case 'SET_MESSAGES':
      return {
        ...state,
        messages: action.payload,
      }
    case 'CLEAR_MESSAGES':
      return {
        ...state,
        messages: [],
      }
    case 'SET_TYPING':
      return {
        ...state,
        isTyping: action.payload,
      }
    default:
      return state
  }
}

// Context
interface ChatContextType {
  state: ChatState
  sendMessage: (content: string) => Promise<void>
  loadChatHistory: (chatbotId: string) => Promise<void>
  clearMessages: () => void
  setCurrentChatbot: (chatbot: Chatbot | null) => void
}

const ChatContext = createContext<ChatContextType | undefined>(undefined)

// Provider component
export const ChatProvider: React.FC<{ children: React.ReactNode }> = ({ children }) => {
  const [state, dispatch] = useReducer(chatReducer, initialState)
  const { token } = useAuth()

  const sendMessage = async (content: string) => {
    if (!content.trim() || !state.currentChatbot) return

    const userMessage: Message = {
      id: Date.now().toString(),
      content: content.trim(),
      role: 'user',
      timestamp: new Date(),
      chatbotId: state.currentChatbot.id,
    }

    dispatch({ type: 'ADD_MESSAGE', payload: userMessage })
    dispatch({ type: 'SET_TYPING', payload: true })

          try {
        // TODO: Replace with actual API call
        const response = await fetch('/api/chat/send', {
          method: 'POST',
          headers: {
            'Content-Type': 'application/json',
            'Authorization': `Bearer ${token}`,
          },
        body: JSON.stringify({
          message: content,
          chatbotId: state.currentChatbot.id,
        }),
      })

      if (!response.ok) {
        throw new Error('Failed to send message')
      }

      const data = await response.json()
      
      const assistantMessage: Message = {
        id: (Date.now() + 1).toString(),
        content: data.response,
        role: 'assistant',
        timestamp: new Date(),
        chatbotId: state.currentChatbot.id,
      }

      dispatch({ type: 'ADD_MESSAGE', payload: assistantMessage })
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to send message',
      })
    } finally {
      dispatch({ type: 'SET_TYPING', payload: false })
    }
  }

  const loadChatHistory = async (chatbotId: string) => {
    dispatch({ type: 'SET_LOADING', payload: true })
    
    try {
      // TODO: Replace with actual API call
      const response = await fetch(`/api/chat/history/${chatbotId}`, {
        headers: {
          'Authorization': `Bearer ${token}`,
        },
      })

      if (!response.ok) {
        throw new Error('Failed to load chat history')
      }

      const data = await response.json()
      dispatch({ type: 'SET_MESSAGES', payload: data.messages })
    } catch (error) {
      dispatch({
        type: 'SET_ERROR',
        payload: error instanceof Error ? error.message : 'Failed to load chat history',
      })
    } finally {
      dispatch({ type: 'SET_LOADING', payload: false })
    }
  }

  const clearMessages = () => {
    dispatch({ type: 'CLEAR_MESSAGES' })
  }

  const setCurrentChatbot = (chatbot: Chatbot | null) => {
    dispatch({ type: 'SET_CURRENT_CHATBOT', payload: chatbot })
  }

  const value = {
    state,
    sendMessage,
    loadChatHistory,
    clearMessages,
    setCurrentChatbot,
  }

  return <ChatContext.Provider value={value}>{children}</ChatContext.Provider>
}

// Hook
export const useChat = () => {
  const context = useContext(ChatContext)
  if (context === undefined) {
    throw new Error('useChat must be used within a ChatProvider')
  }
  return context
} 